import { useState, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  BookOpen, Search, ChevronRight, Download, LayoutDashboard, FileBarChart, Users,
  ShoppingCart, MessageSquare, HelpCircle, Package, Truck, CreditCard, ClipboardList,
  Shield, Building, BarChart3, Settings, LogIn, UserPlus, Key, Globe, Headphones,
  Calendar, FileText, Upload, Star, Share2, Printer, Eye, PenLine, Trash2, Plus,
  Filter, RefreshCw, Bell, Mail, ArrowLeft, CheckCircle, AlertTriangle, Clock, Layers
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ===== MODULE DATA =====
const quickGuideModules = [
  "inicio-sesion", "dashboard", "cotizaciones", "gestion-humana",
  "requisiciones", "mensajeria", "whatsapp", "mesa-ayuda"
];

const modules = [
  {
    id: "inicio-sesion",
    title: "Inicio de Sesión",
    icon: LogIn,
    color: "#193966",
    description: "Acceda a la plataforma de manera segura",
    steps: [
      { title: "Acceder a la plataforma", text: "Abra su navegador y vaya a la dirección web proporcionada por su administrador." },
      { title: "Ingresar credenciales", text: "Escriba su correo electrónico en el campo 'Email' y su contraseña en el campo 'Contraseña'." },
      { title: "Iniciar sesión", text: "Haga clic en el botón 'Iniciar Sesión'. Si sus credenciales son correctas, será redirigido al Dashboard principal." },
      { title: "Cambiar contraseña", text: "Si es su primer inicio o necesita cambiarla, vaya a 'Cambiar Contraseña' en el menú lateral inferior." },
    ],
    tips: ["Si olvida su contraseña, contacte al administrador del sistema.", "Su sesión permanece activa hasta que cierre sesión manualmente."],
  },
  {
    id: "dashboard",
    title: "Dashboard Principal",
    icon: LayoutDashboard,
    color: "#2563eb",
    description: "Vista general de la plataforma y accesos rápidos",
    steps: [
      { title: "Panel de bienvenida", text: "Al ingresar verá el saludo personalizado con su nombre, el panel de control de comparaciones de pólizas, tasas de cambio y estadísticas generales." },
      { title: "Estadísticas rápidas", text: "Las tarjetas superiores muestran: Total Comparaciones, Informes Generados, Documentos subidos y Tiempo Ahorrado con IA." },
      { title: "Comparaciones recientes", text: "En la parte inferior encontrará la lista de comparaciones de documentos recientes con su estado (Completado, En proceso, Error)." },
      { title: "Mi Dashboard", text: "Acceda a 'Mi Dashboard' desde el menú lateral para ver un resumen personalizado de sus tareas, pendientes y actividad reciente." },
      { title: "Dashboard Ejecutivo", text: "Los administradores pueden acceder al 'Dashboard Ejecutivo' para ver KPIs generales de la organización, métricas de rendimiento y gráficos de tendencia." },
    ],
    tips: ["Las tasas de cambio USD y EUR se actualizan automáticamente.", "Use 'Nueva Comparación' para iniciar un análisis rápido de documentos."],
  },
  {
    id: "cotizaciones",
    title: "Cotizaciones de Seguros",
    icon: FileBarChart,
    color: "#059669",
    description: "Gestión y comparación de propuestas de seguros con inteligencia artificial",
    steps: [
      { title: "Crear nueva cotización", text: "Haga clic en '+ Nueva Cotización'. Complete el formulario con: Título, Cliente, Departamento (Salud Local, Internacional o Riesgos Generales), Ramo y datos del cliente." },
      { title: "Cargar documentos", text: "Dentro de la cotización, use 'Cargar Documento' para subir PDFs o Excel de propuestas de aseguradoras. Puede arrastrar y soltar archivos (Drag & Drop). La IA extraerá automáticamente toda la información: primas, coberturas, deducibles, beneficios." },
      { title: "Agregar propuesta manual", text: "Use '+ Agregar Manual' para ingresar datos de una propuesta manualmente si no dispone del documento digital." },
      { title: "Analizar con IA", text: "Con 2 o más propuestas cargadas, haga clic en 'Analizar con IA'. La inteligencia artificial comparará todas las propuestas y generará: un Ranking con puntajes, Comparativa de Coberturas, Comparativa de Primas y una Recomendación profesional." },
      { title: "Pestaña Comparativo", text: "Muestra las tarjetas de cada propuesta con: Prima Anual, Deducible, Coaseguro, Tope de Cobertura, y listado de coberturas con badge expandible '+N más'." },
      { title: "Pestaña Análisis IA", text: "Muestra el Dictamen IA con la recomendación global, las tarjetas de Mejor Opción, Mejor Precio y Mejores Coberturas, seguido del Ranking detallado con puntajes por criterio." },
      { title: "Pestaña Gráficos", text: "Visualice gráficos de barras comparativos, radar multicriteria y la tabla comparativa de coberturas agrupada por aseguradora y plan." },
      { title: "Exportar a Excel", text: "Haga clic en 'Excel' para descargar un comparativo profesional en formato .xlsx con logo, colores institucionales y sin líneas de celda." },
      { title: "Personalizar Cotización", text: "Use 'Personalizar Cotización' para generar un informe ejecutivo en PDF o Word (DOCX) con datos del cliente, portada profesional, resumen ejecutivo, ranking, comparativas y recomendación." },
      { title: "Compartir cotización", text: "Haga clic en 'Compartir' para compartir la cotización con otros usuarios de la plataforma. Seleccione los usuarios y ellos podrán ver la cotización desde su listado." },
      { title: "Configuración (Admin)", text: "En la pestaña 'Configuración', los administradores pueden gestionar: Aseguradoras (agregar/editar/eliminar), Coberturas (con departamento y badge IA), Deducibles comunes y Planes." },
    ],
    tips: [
      "La IA puede leer PDFs y archivos Excel directamente, no es necesario copiar/pegar texto.",
      "Haga doble clic en una aseguradora o cobertura en Configuración para editarla inline.",
      "Las coberturas se auto-alimentan desde los documentos que sube la IA.",
      "Use el filtro de departamento para encontrar cotizaciones rápidamente.",
    ],
  },
  {
    id: "gestion-humana",
    title: "Gestión Humana",
    icon: Users,
    color: "#7c3aed",
    description: "Administración de empleados, vacaciones, reclutamiento y nómina",
    steps: [
      { title: "Empleados", text: "Vaya a Gestión Humana > Empleados para ver el listado completo. Puede agregar nuevos empleados, editar información, ver historial y gestionar documentos de cada colaborador." },
      { title: "Vacaciones", text: "En Gestión Humana > Vacaciones, los empleados pueden solicitar vacaciones y los supervisores aprobarlas. Se muestra el balance de días disponibles, días tomados y pendientes." },
      { title: "Permisos", text: "Gestione solicitudes de permisos (médicos, personales, etc.) con flujo de aprobación. Cada permiso queda registrado con fecha, tipo y estado." },
      { title: "Reclutamiento", text: "Administre el proceso de reclutamiento: publicar vacantes, registrar candidatos, programar entrevistas y dar seguimiento hasta la contratación." },
      { title: "Solicitudes unificadas", text: "Los empleados pueden crear solicitudes de diferentes tipos (cartas, constancias, avances) desde un formulario unificado." },
      { title: "Movimientos", text: "Registre y consulte movimientos de personal: ascensos, cambios de departamento, aumentos salariales, etc." },
      { title: "Nómina", text: "Gestione la información de nómina, bonificaciones y deducciones de cada empleado." },
      { title: "Dashboard RRHH", text: "Panel ejecutivo con métricas de recursos humanos: headcount, rotación, distribución por departamento, cumpleaños del mes." },
    ],
    tips: [
      "Los cumpleaños del mes se muestran automáticamente en la sección Cumpleaños.",
      "Las tardanzas se registran y pueden generar alertas automáticas.",
      "Use Procesos Internos para gestionar flujos de trabajo personalizados.",
    ],
  },
  {
    id: "requisiciones",
    title: "Requisiciones de Materiales",
    icon: ShoppingCart,
    color: "#dc2626",
    description: "Solicitudes de compra con flujo de aprobación",
    steps: [
      { title: "Nueva requisición", text: "Haga clic en '+ Nueva Requisición'. Complete: Justificación, Fecha requerida y agregue los artículos necesarios con cantidad y especificaciones." },
      { title: "Mis requisiciones", text: "En 'Mis Requisiciones' vea el estado de todas sus solicitudes: Pendiente, Aprobada, Parcial, Entregada o Rechazada." },
      { title: "Aprobar requisiciones", text: "Los aprobadores verán las requisiciones pendientes en 'Por Aprobar'. Pueden aprobar, rechazar o solicitar modificaciones." },
      { title: "Historial", text: "Consulte el historial completo de todas las requisiciones con filtros por fecha, estado y solicitante." },
    ],
    tips: [
      "Los artículos se toman del catálogo de inventario si está configurado.",
      "Las requisiciones aprobadas generan notificaciones automáticas.",
      "Use los filtros por estado para encontrar requisiciones rápidamente.",
    ],
  },
  {
    id: "cuentas-pagar",
    title: "Cuentas por Pagar",
    icon: CreditCard,
    color: "#ea580c",
    description: "Gestión de facturas, proveedores y pagos pendientes",
    steps: [
      { title: "Nueva factura", text: "Haga clic en '+ Nueva Factura'. Ingrese: Número de factura, NCF, Proveedor, Categoría, Monto, Moneda y Fecha de vencimiento." },
      { title: "Panel de control", text: "Las tarjetas superiores muestran: Total Pendiente, Monto Vencido, Próximas a vencer (7 días) y Pagado este mes." },
      { title: "Registrar pago", text: "En cada factura, use el icono de pago para registrar abonos parciales o pago total. El estado se actualiza automáticamente (Pendiente, Parcial, Pagada)." },
      { title: "Proveedores", text: "Gestione su catálogo de proveedores con datos de contacto, RNC y categorización." },
      { title: "Alertas de pago", text: "Configure alertas automáticas para facturas próximas a vencer o ya vencidas." },
      { title: "Exportar", text: "Use 'Exportar Excel' para descargar el listado completo de facturas con filtros aplicados." },
    ],
    tips: [
      "Las facturas vencidas se destacan en rojo automáticamente.",
      "Configure la pestaña 'Configuración' para personalizar categorías y términos de pago.",
    ],
  },
  {
    id: "inventario",
    title: "Inventario de Materiales",
    icon: Package,
    color: "#0891b2",
    description: "Control de stock, entradas, salidas y movimientos",
    steps: [
      { title: "Dashboard de inventario", text: "Vista general con: valor total del inventario, artículos en stock, artículos bajo mínimo y movimientos recientes." },
      { title: "Artículos", text: "Gestione el catálogo de artículos con: nombre, código, categoría, unidad de medida, stock mínimo y ubicación." },
      { title: "Entradas", text: "Registre entradas de mercancía al almacén con: proveedor, factura, artículos y cantidades. El stock se actualiza automáticamente." },
      { title: "Salidas", text: "Registre salidas/despachos con: departamento solicitante, artículos y cantidades. Se descuenta del stock." },
      { title: "Movimientos", text: "Consulte el historial completo de todos los movimientos (entradas, salidas, ajustes) con filtros por fecha y tipo." },
      { title: "Categorías", text: "Organice los artículos en categorías para una mejor gestión." },
    ],
    tips: [
      "Los artículos con stock bajo mínimo se resaltan automáticamente.",
      "Use la auditoría de inventario para verificaciones periódicas.",
    ],
  },
  {
    id: "mensajeria",
    title: "Mensajería y Entregas",
    icon: Truck,
    color: "#c026d3",
    description: "Gestión de envíos de documentos y paquetes con mensajeros",
    steps: [
      { title: "Nueva solicitud", text: "Haga clic en '+ Nueva Solicitud'. Complete: Cliente, Dirección de entrega, Tipo de documento, Descripción y asigne un mensajero." },
      { title: "Panel de solicitudes", text: "Las tarjetas superiores muestran estadísticas: Total, Pendientes, En Proceso, Completados, Devueltos y Urgentes. Debajo se ven los mensajeros activos." },
      { title: "Gestionar entregas", text: "Use 'Iniciar Grupo' para que el mensajero comience su ruta. 'Completar Grupo' cuando termine todas las entregas del grupo." },
      { title: "Mensajeros", text: "Registre y administre a los mensajeros disponibles con asignación de zonas." },
      { title: "Chat interno", text: "Use el Chat para comunicarse con los mensajeros en tiempo real sobre las entregas." },
      { title: "Plantillas", text: "Configure plantillas de documentos frecuentes para agilizar la creación de solicitudes." },
      { title: "Importar Excel", text: "Use 'Importar Excel' para cargar solicitudes masivas desde una hoja de cálculo." },
    ],
    tips: [
      "Las solicitudes urgentes se destacan con indicador visual.",
      "Use la correspondencia interna para envíos entre sucursales.",
    ],
  },
  {
    id: "whatsapp",
    title: "WhatsApp Business",
    icon: MessageSquare,
    color: "#16a34a",
    description: "Bandeja de conversaciones de WhatsApp integrada",
    steps: [
      { title: "Bandeja de entrada", text: "Vea todas las conversaciones de WhatsApp en la bandeja unificada. Las conversaciones no leídas se resaltan." },
      { title: "Responder mensajes", text: "Seleccione una conversación para ver el historial y responder. Puede enviar texto, imágenes y documentos." },
      { title: "Asignaciones", text: "Asigne conversaciones a agentes específicos para mejor seguimiento." },
      { title: "Respuestas rápidas", text: "Configure respuestas predefinidas para preguntas frecuentes y agilizar la atención." },
      { title: "Plantillas", text: "Use plantillas aprobadas de WhatsApp para mensajes proactivos (recordatorios, notificaciones)." },
      { title: "Chatbot", text: "Configure respuestas automáticas del chatbot para atención fuera de horario o preguntas frecuentes." },
      { title: "Contactos", text: "Gestione el directorio de contactos de WhatsApp con etiquetas y segmentación." },
    ],
    tips: [
      "Las conversaciones se actualizan en tiempo real.",
      "Configure el chatbot para manejar consultas básicas automáticamente.",
    ],
  },
  {
    id: "mesa-ayuda",
    title: "Mesa de Ayuda",
    icon: Headphones,
    color: "#4f46e5",
    description: "Sistema de tickets para soporte interno",
    steps: [
      { title: "Crear ticket", text: "Los usuarios pueden crear tickets de soporte describiendo su problema o solicitud, asignando categoría y prioridad." },
      { title: "Mis tickets", text: "En 'Mis Tickets' cada usuario ve sus tickets abiertos con estado actualizado." },
      { title: "Gestionar tickets", text: "Los agentes de soporte ven todos los tickets en la bandeja principal. Pueden: asignarse tickets, cambiar prioridad, agregar comentarios y resolver." },
      { title: "Categorías", text: "Configure categorías de tickets (Hardware, Software, Red, etc.) para clasificar los problemas." },
      { title: "Prioridades", text: "Defina niveles de prioridad con colores y SLAs (Baja, Normal, Alta, Crítica)." },
    ],
    tips: [
      "Los tickets críticos generan notificaciones inmediatas.",
      "Use plantillas de respuesta para soluciones frecuentes.",
    ],
  },
  {
    id: "control-renovaciones",
    title: "Control de Renovaciones",
    icon: Calendar,
    color: "#b45309",
    description: "Seguimiento de pólizas próximas a renovar",
    steps: [
      { title: "Vista de renovaciones", text: "Vea todas las pólizas con su fecha de vencimiento, estado y días restantes para la renovación." },
      { title: "Filtrar por estado", text: "Use los filtros para ver: Próximas a vencer, Vencidas, Renovadas o Todas." },
      { title: "Gestionar renovación", text: "Haga clic en una póliza para ver los detalles y gestionar el proceso de renovación." },
    ],
    tips: ["Las alertas automáticas notifican pólizas próximas a vencer."],
  },
  {
    id: "encuestas",
    title: "Encuestas",
    icon: ClipboardList,
    color: "#0d9488",
    description: "Creación y gestión de encuestas de satisfacción",
    steps: [
      { title: "Portal de encuestas", text: "Acceda al portal para crear y gestionar encuestas. Vea estadísticas de respuestas y participación." },
      { title: "Crear encuesta", text: "Configure encuestas con diferentes tipos de preguntas: opción múltiple, escala de satisfacción, texto libre, etc." },
      { title: "Campañas", text: "Cree campañas de encuestas para enviarlas a grupos específicos de destinatarios por correo electrónico." },
      { title: "Resultados", text: "Visualice los resultados con gráficos, porcentajes y análisis detallado de cada pregunta." },
      { title: "Scheduler", text: "Programe encuestas para enviarse automáticamente en fechas específicas o de forma periódica." },
    ],
    tips: [
      "Las encuestas generan un enlace público que puede compartirse por cualquier medio.",
      "Use el análisis IA para interpretar los resultados automáticamente.",
    ],
  },
  {
    id: "visitantes",
    title: "Control de Visitantes",
    icon: Building,
    color: "#9333ea",
    description: "Registro y seguimiento de visitantes en las instalaciones",
    steps: [
      { title: "Registrar entrada", text: "Haga clic en 'Registrar Entrada'. Ingrese: nombre del visitante, cédula, empresa, persona a visitar y motivo." },
      { title: "Panel de control", text: "Las tarjetas muestran: Visitas Hoy, En el Edificio, Tiempo Promedio de visita y Esta Semana." },
      { title: "Registrar salida", text: "Cuando el visitante se retira, haga clic en 'Salida' para registrar la hora de salida." },
      { title: "Historial", text: "Consulte el historial completo de visitas con filtros por fecha, visitante y empresa." },
      { title: "Tipos de visita", text: "Configure los tipos de visita (Reunión, Entrevista, Entrega, etc.) para clasificar las visitas." },
      { title: "Alertas", text: "Configure alertas para visitantes con tiempo excedido en las instalaciones." },
      { title: "Reportes", text: "Genere reportes de visitas por período, empresa o persona visitada." },
    ],
    tips: [
      "Los visitantes con tiempo excedido se resaltan automáticamente con alerta.",
      "Se puede buscar visitantes por nombre, cédula o empresa.",
    ],
  },
  {
    id: "configuracion",
    title: "Configuración del Sistema",
    icon: Settings,
    color: "#475569",
    description: "Ajustes generales, usuarios, permisos y personalización",
    steps: [
      { title: "Usuarios", text: "Gestione los usuarios de la plataforma: crear nuevos, editar roles, activar/desactivar cuentas." },
      { title: "Permisos", text: "Configure los permisos de acceso por rol: qué módulos y acciones puede realizar cada tipo de usuario." },
      { title: "Departamentos", text: "Administre la estructura organizacional con departamentos y sus jerarquías." },
      { title: "Menús", text: "Personalice los menús del sidebar: agregue, oculte o reordene las opciones de navegación." },
      { title: "Plantillas de correo", text: "Configure las plantillas de correo electrónico para cada tipo de notificación del sistema." },
      { title: "Notificaciones", text: "Configure qué eventos generan notificaciones y a quiénes se envían." },
      { title: "Personalización", text: "Ajuste colores, logo y apariencia general de la plataforma." },
      { title: "Auditoría", text: "Consulte el log de auditoría con todas las acciones realizadas en el sistema." },
      { title: "Firma digital", text: "Configure la firma digital institucional para documentos generados." },
      { title: "Backup", text: "Gestione copias de seguridad de la base de datos." },
    ],
    tips: [
      "Los cambios de permisos se aplican inmediatamente al iniciar sesión.",
      "Use la auditoría para investigar acciones específicas de usuarios.",
      "Las reglas de alertas permiten automatizar notificaciones complejas.",
    ],
  },
  {
    id: "caja-chica",
    title: "Caja Chica",
    icon: CreditCard,
    color: "#65a30d",
    description: "Control de gastos menores y fondos de caja chica",
    steps: [
      { title: "Registrar gasto", text: "Registre gastos menores con: concepto, monto, fecha, categoría y comprobante." },
      { title: "Balance", text: "Vea el balance actual de la caja chica, gastos acumulados y fondo disponible." },
      { title: "Reposición", text: "Solicite la reposición del fondo cuando el balance esté bajo." },
      { title: "Configuración", text: "Configure el monto inicial del fondo, categorías de gasto y aprobadores." },
    ],
    tips: ["Los gastos requieren justificación y pueden requerir aprobación según el monto."],
  },
  {
    id: "mobiliario",
    title: "Mobiliario y Activos",
    icon: Package,
    color: "#9333ea",
    description: "Gestión de activos fijos, asignaciones y mantenimiento",
    steps: [
      { title: "Dashboard", text: "Vista general con: total de activos, valor total, activos asignados y próximos mantenimientos." },
      { title: "Activos", text: "Registre activos fijos con: nombre, código, categoría, ubicación, valor y estado." },
      { title: "Asignaciones", text: "Asigne activos a empleados o departamentos con registro de entrega." },
      { title: "Mantenimiento", text: "Programe y registre mantenimientos preventivos y correctivos de los activos." },
      { title: "Bajas", text: "Registre la baja de activos por deterioro, pérdida o venta." },
    ],
    tips: ["Use las categorías y ubicaciones para organizar mejor los activos."],
  },
];

function ManualSection({ module, isQuickGuide }) {
  const Icon = module.icon;
  return (
    <div className="space-y-6" data-testid={`manual-section-${module.id}`}>
      <div className="flex items-center gap-3 pb-4 border-b border-slate-200">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: module.color + "15" }}>
          <Icon className="w-6 h-6" style={{ color: module.color }} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">{module.title}</h2>
          <p className="text-sm text-slate-500">{module.description}</p>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Pasos</h3>
        {module.steps.map((step, i) => (
          <div key={i} className="flex gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white shrink-0" style={{ backgroundColor: module.color }}>
              {i + 1}
            </div>
            <div>
              <p className="font-semibold text-slate-700 mb-1">{step.title}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{step.text}</p>
            </div>
          </div>
        ))}
      </div>

      {module.tips?.length > 0 && (
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
            <Star className="w-4 h-4" /> Consejos útiles
          </h3>
          <ul className="space-y-1.5">
            {module.tips.map((tip, i) => (
              <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Mapping from manual module IDs to sidebar menu labels for permission filtering
const moduleToSidebarLabel = {
  "dashboard": "Mi Dashboard",
  "cotizaciones": "Cotizaciones",
  "gestion-humana": "Gestión Humana",
  "requisiciones": "Requisiciones",
  "cuentas-pagar": "Finanzas y Contabilidad",
  "inventario": "Finanzas y Contabilidad",
  "mensajeria": "Mensajería",
  "whatsapp": "WhatsApp",
  "mesa-ayuda": "Mesa de Ayuda",
  "control-renovaciones": "Riesgos Generales",
  "encuestas": "Modulo de encuesta",
  "visitantes": "Control Visitantes",
  "configuracion": "Configuración",
  "caja-chica": "Finanzas y Contabilidad",
  "mobiliario": "Finanzas y Contabilidad",
};

// "inicio-sesion" is always visible (no permission needed)

export default function ManualPage() {
  const { user, sidebarMenus } = useAuth();
  const [activeTab, setActiveTab] = useState("rapida");
  const [activeModule, setActiveModule] = useState("inicio-sesion");
  const [searchTerm, setSearchTerm] = useState("");

  // Permission check - same logic as Sidebar's hasAccess
  const hasAccess = (permissionKey) => {
    const userRole = (user?.role || "").toLowerCase();
    if (userRole === "admin" || userRole === "administrador") return true;
    if (!user?.menu_permissions) return false;
    const perm = user.menu_permissions[permissionKey];
    if (perm?.access === false) return false;
    if (perm?.access === true) return true;
    return false;
  };

  // Get labels of menus the user actually has access to (applying menu_permissions)
  const allowedLabels = useMemo(() => {
    if (!sidebarMenus || sidebarMenus.length === 0) return [];
    const userRole = (user?.role || "").toLowerCase();
    const isAdmin = userRole === "admin" || userRole === "administrador";
    return sidebarMenus
      .filter(menu => {
        if (isAdmin) return true;
        if (hasAccess(menu.id)) return true;
        const accessibleSubs = (menu.submenus || []).filter(sub => hasAccess(sub.id));
        return accessibleSubs.length > 0;
      })
      .map(m => m.label);
  }, [sidebarMenus, user]);

  // Módulos fijos ENRIQUECIDOS: submenús nuevos dentro de menús ya cubiertos
  // se agregan como pasos al final de su sección (se actualizan solos).
  const enrichedModules = useMemo(() => {
    if (!sidebarMenus || sidebarMenus.length === 0) return modules;
    const clones = modules.map(m => ({ ...m, steps: [...m.steps] }));
    const byLabel = {};
    clones.forEach(m => {
      const label = moduleToSidebarLabel[m.id];
      if (label) (byLabel[label] = byLabel[label] || []).push(m);
    });
    sidebarMenus.forEach(menu => {
      const mods = byLabel[(menu.label || "").trim()];
      if (!mods) return;
      const texto = mods.map(mm => mm.title + " " + mm.steps.map(s => s.title + " " + s.text).join(" ")).join(" ").toLowerCase();
      (menu.submenus || []).forEach(sub => {
        const sl = (sub.label || "").trim();
        if (sl && !texto.includes(sl.toLowerCase())) {
          mods[0].steps.push({ title: sl, text: `Acceda desde el menú lateral: ${menu.label} → ${sl}.` });
        }
      });
    });
    return clones;
  }, [sidebarMenus]);

  // Filter modules by user permissions
  const permittedModules = useMemo(() => {
    return enrichedModules.filter(m => {
      if (m.id === "inicio-sesion") return true; // always show login guide
      const sidebarLabel = moduleToSidebarLabel[m.id];
      if (!sidebarLabel) return false; // no mapping = don't show
      return allowedLabels.includes(sidebarLabel);
    });
  }, [allowedLabels, enrichedModules]);

  // Módulos DINÁMICOS: menús/submenús creados en Configuración → Menús que no
  // tienen sección fija. Se actualizan solos al crear o eliminar menús.
  const dynamicModules = useMemo(() => {
    if (!sidebarMenus || sidebarMenus.length === 0) return [];
    const coveredLabels = new Set(Object.values(moduleToSidebarLabel));
    const userRole = (user?.role || "").toLowerCase();
    const isAdmin = userRole === "admin" || userRole === "administrador";
    return sidebarMenus
      .filter(menu => menu.label && !coveredLabels.has(menu.label))
      .filter(menu => {
        if (isAdmin) return true;
        if (hasAccess(menu.id)) return true;
        return (menu.submenus || []).some(sub => hasAccess(sub.id));
      })
      .map(menu => {
        const subs = (menu.submenus || []).filter(sub => isAdmin || hasAccess(menu.id) || hasAccess(sub.id));
        return {
          id: `menu-${menu.id}`,
          title: menu.label,
          icon: Layers,
          color: "#0C314C",
          description: `Módulo ${menu.label} de la plataforma`,
          steps: subs.length > 0
            ? subs.map(sub => ({ title: sub.label, text: `Acceda desde el menú lateral: ${menu.label} → ${sub.label}.` }))
            : [{ title: `Acceder a ${menu.label}`, text: `Haga clic en '${menu.label}' en el menú lateral para abrir el módulo.` }],
          tips: [],
        };
      });
  }, [sidebarMenus, user]);

  const allManualModules = useMemo(() => [...permittedModules, ...dynamicModules], [permittedModules, dynamicModules]);

  const displayModules = activeTab === "rapida"
    ? permittedModules.filter(m => quickGuideModules.includes(m.id))
    : allManualModules;

  const filteredModules = searchTerm
    ? displayModules.filter(m =>
        m.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.steps.some(s => s.title.toLowerCase().includes(searchTerm.toLowerCase()) || s.text.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : displayModules;

  const currentModule = allManualModules.find(m => m.id === activeModule);

  const handleDownload = async (formato) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/manual/download?formato=${formato}&tipo=${activeTab}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Manual_QAsesores_${activeTab === "rapida" ? "Guia_Rapida" : "Completo"}.${formato}`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Error downloading manual:", err);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="manual-page">
      <Sidebar />
      <div className="ml-0 md:ml-64">
      {/* Header */}
      <div className="border-b border-slate-200 bg-slate-50 px-6 py-5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#193966] flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Manual de Usuario</h1>
              <p className="text-sm text-slate-500">QAsesores — Guía paso a paso de la plataforma</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => handleDownload("pdf")} className="gap-1.5" data-testid="download-pdf-btn">
              <Download className="w-4 h-4" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleDownload("docx")} className="gap-1.5" data-testid="download-docx-btn">
              <Download className="w-4 h-4" /> Word
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto flex">
        {/* Sidebar */}
        <div className="w-72 border-r border-slate-200 min-h-[calc(100vh-80px)] bg-white p-4 shrink-0">
          {/* Tab switcher */}
          <div className="flex rounded-lg bg-slate-100 p-1 mb-4">
            <button
              onClick={() => { setActiveTab("rapida"); setActiveModule(quickGuideModules[0]); }}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${activeTab === "rapida" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}
              data-testid="tab-guia-rapida"
            >
              Guía Rápida
            </button>
            <button
              onClick={() => { setActiveTab("completo"); setActiveModule("inicio-sesion"); }}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${activeTab === "completo" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}
              data-testid="tab-manual-completo"
            >
              Manual Completo
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-9 text-sm bg-slate-50 border-slate-200"
              data-testid="manual-search"
            />
          </div>

          {/* Module list */}
          <nav className="space-y-0.5">
            {filteredModules.map(m => {
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  onClick={() => setActiveModule(m.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                    activeModule === m.id ? "bg-slate-100 text-slate-900 font-medium" : "text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`nav-${m.id}`}
                >
                  <Icon className="w-4 h-4 shrink-0" style={{ color: m.color }} />
                  <span className="truncate">{m.title}</span>
                  {activeModule === m.id && <ChevronRight className="w-3.5 h-3.5 ml-auto text-slate-400" />}
                </button>
              );
            })}
          </nav>

          {activeTab === "rapida" && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs text-blue-700">
                <strong>Guía Rápida:</strong> Incluye los {quickGuideModules.length} módulos más utilizados.
                Cambie a "Manual Completo" para ver todos los {allManualModules.length} módulos.
              </p>
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 p-8 max-w-3xl">
          {currentModule && <ManualSection module={currentModule} isQuickGuide={activeTab === "rapida"} />}
        </div>
      </div>
      </div>
    </div>
  );
}
